// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Color } from '../../../Graphics';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { MAMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
import { InputParameterDouble } from '../InputParamaterClasses/InputParameterDouble';
import { InputParameterBoolean } from '../InputParamaterClasses/InputParameterBoolean';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
export var MAType;
(function (MAType) {
    MAType[MAType["EMA"] = 0] = "EMA";
    MAType[MAType["SMA"] = 1] = "SMA";
    MAType[MAType["HMA"] = 2] = "HMA";
    MAType[MAType["WMA"] = 3] = "WMA";
    MAType[MAType["DEMA"] = 4] = "DEMA";
})(MAType || (MAType = {}));
export class RVOL extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'Relative Volume';
        this.SetIndicatorLine('volume_positive', Color.Teal, 2, LineStyle.HistogrammChart);
        this.SetIndicatorLine('volume_negative', Color.Gray, 2, LineStyle.HistogrammChart);
        this.SetIndicatorLine('ma', Color.Orange, 2, LineStyle.SimpleChart);
        this.SetIndicatorLine('rvol', Color.CadetBlue, 2, LineStyle.SimpleChart);
        this.SetIndicatorLine('volume_threshold', Color.Blue, 2, LineStyle.SimpleChart);
        this.SetIndicatorLine('rvol_threshold', Color.LightGreen, 2, LineStyle.SimpleChart);
        this.SetIndicatorLine('lookback_threshold', Color.Red, 2, LineStyle.SimpleChart);
        this.SeparateWindow = true;
        this.lookback = 50;
        super.InputParameter(new InputParameterInteger('lookback', 'Highest Volume Lookback Period', 0, 1, 99999));
        this.maLength = 50;
        super.InputParameter(new InputParameterInteger('maLength', 'MA Length', 1, 1, 999));
        this.maType = MAType.EMA;
        super.InputParameter(new InputParameterCombobox('maType', 'MA Type', 2, [
            { EMA: MAType.EMA },
            { SMA: MAType.SMA },
            { HMA: MAType.HMA },
            { WMA: MAType.WMA },
            { DEMA: MAType.DEMA }
        ]));
        this.volTrigger = 0;
        super.InputParameter(new InputParameterInteger('volTrigger', 'Absolute Volume Level To Trigger Signal', 3, 0, 999));
        this.rvolTrigger = 2.5;
        super.InputParameter(new InputParameterDouble('rvolTrigger', 'Absolute RVOL Level To Trigger Signal', 4, 0, 999, 1, 0.1));
        this.useVol = false;
        super.InputParameter(new InputParameterBoolean('useVol', 'Use Static Volume For Alerts?', 5));
        this.useRVOL = false;
        super.InputParameter(new InputParameterBoolean('useRVOL', 'Use RVOL For Alerts?', 6));
        this.useLookback = false;
        super.InputParameter(new InputParameterBoolean('useLookback', 'Use Lookback For Alerts?', 7));
        this.showRVOL = true;
        super.InputParameter(new InputParameterBoolean('showRVOL', 'Draw RVOL Threshold?', 8));
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
    }
    createInternalIndicators() {
        const indicators = [];
        const volumeAccessor = (offset) => {
            return this.GetVolume(offset);
        };
        switch (this.maType) {
            case MAType.SMA: {
                this.maIndicator = this.Indicators.iMAOnArray(this.CurrentData, volumeAccessor, this.maLength, MAMode.SMA);
                indicators.push(this.maIndicator);
                break;
            }
            case MAType.EMA: {
                this.maIndicator = this.Indicators.iMAExOnArray(this.CurrentData, volumeAccessor, this.maLength, MAMode.EMA, 0, 0);
                indicators.push(this.maIndicator);
                break;
            }
            case MAType.WMA: {
                this.maIndicator = this.Indicators.iMAOnArray(this.CurrentData, volumeAccessor, this.maLength, MAMode.LWMA);
                indicators.push(this.maIndicator);
                break;
            }
            case MAType.DEMA: {
                this.demaEma1Indicator = this.Indicators.iMAExOnArray(this.CurrentData, volumeAccessor, this.maLength, MAMode.EMA, 0, 0);
                const ema1Accessor = (offset) => {
                    return this.demaEma1Indicator.GetValue(offset);
                };
                this.demaEma2Indicator = this.Indicators.iMAExOnArray(this.CurrentData, ema1Accessor, this.maLength, MAMode.EMA, 0, 0);
                indicators.push(this.demaEma1Indicator, this.demaEma2Indicator);
                break;
            }
            case MAType.HMA: {
                const halfPeriod = Math.max(1, Math.floor(this.maLength / 2));
                const sqrtPeriod = Math.max(1, Math.floor(Math.sqrt(this.maLength)));
                this.hmaFullIndicator = this.Indicators.iMAOnArray(this.CurrentData, volumeAccessor, this.maLength, MAMode.LWMA);
                this.hmaHalfIndicator = this.Indicators.iMAOnArray(this.CurrentData, volumeAccessor, halfPeriod, MAMode.LWMA);
                const dsAccessor = (offset) => {
                    return 2 * this.hmaHalfIndicator.GetValue(offset) - this.hmaFullIndicator.GetValue(offset);
                };
                this.hmaFinalIndicator = this.Indicators.iMAOnArray(this.CurrentData, dsAccessor, sqrtPeriod, MAMode.LWMA);
                indicators.push(this.hmaFullIndicator, this.hmaHalfIndicator, this.hmaFinalIndicator);
                break;
            }
            default:
                break;
        }
        return indicators;
    }
    GetIndicatorShortName() {
        return 'Relative Volume';
    }
    UpdateIndexDrawBegin() {
        this.SetIndexDrawBegin(0, this.maLength);
        this.SetIndexDrawBegin(1, this.maLength);
        this.SetIndexDrawBegin(2, this.maLength);
        this.SetIndexDrawBegin(3, this.maLength);
        this.SetIndexDrawBegin(4, this.maLength);
        this.SetIndexDrawBegin(5, this.maLength);
        this.SetIndexDrawBegin(6, this.maLength);
    }
    Complete() { }
    NextBar() {
        super.NextBar();
    }
    OnQuote() {
        super.OnQuote();
        if (this.CurrentData.Count < this.maLength) {
            return;
        }
        let _ma = 0;
        switch (this.maType) {
            case MAType.DEMA:
                if (this.demaEma1Indicator && this.demaEma2Indicator) {
                    const ema1 = this.demaEma1Indicator.GetValue();
                    const ema2 = this.demaEma2Indicator.GetValue();
                    _ma = 2 * ema1 - ema2;
                }
                break;
            case MAType.HMA:
                if (this.hmaFinalIndicator) {
                    _ma = this.hmaFinalIndicator.GetValue();
                }
                break;
            case MAType.SMA:
            case MAType.EMA:
            case MAType.WMA:
            default:
                if (this.maIndicator) {
                    _ma = this.maIndicator.GetValue();
                }
                break;
        }
        const rvol = _ma !== 0 ? this.GetVolume() / _ma : 0;
        const highestVolume = this.GetHighestVolume();
        const volAlert = this.useVol && this.GetVolume() >= this.volTrigger;
        const rvolAlert = this.useRVOL && rvol >= this.rvolTrigger;
        const lookbackAlert = this.useLookback && this.GetVolume() >= highestVolume;
        const alert = volAlert || rvolAlert || lookbackAlert;
        if (alert) {
            this.SetValue(0, 0, this.GetVolume());
        }
        else {
            this.SetValue(1, 0, this.GetVolume());
        }
        this.SetValue(2, 0, _ma);
        this.SetValue(3, 0, rvol);
        if (this.showRVOL && this.useVol) {
            this.SetValue(4, 0, this.volTrigger);
        }
        if (this.showRVOL && this.useRVOL) {
            this.SetValue(5, 0, this.rvolTrigger * _ma);
        }
        if (this.showRVOL && this.useLookback) {
            this.SetValue(6, 0, highestVolume);
        }
    }
    GetVolume(x = 0) {
        return this.CurrentData.Volume(x);
    }
    GetHighestVolume() {
        let highest = 0;
        const _lookback = Math.min(this.lookback, this.CurrentData.Count - 1);
        for (let i = 0; i < _lookback; i++) {
            highest = Math.max(this.GetVolume(i), highest);
        }
        return highest;
    }
}
